// Form is based on Formik
// Data validation is based on Yup
// Please, be familiar with article first:
// https://hackernoon.com/react-form-validation-with-formik-and-yup-8b76bda62e10
import GroupIcon from '@mui/icons-material/Group';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Button,
  FormControlLabel,
  MenuItem,
  Tab,
  Theme,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { connect, Formik, FormikProps, FormikValues } from 'formik';
import React, { MutableRefObject, useMemo, useRef, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import TabLabel from 'src/app/components/general/TabLabel';
import { useDebouncedCallback } from 'use-debounce/lib';
import * as Yup from 'yup';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import {
  FormikSelect,
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import '../../../common/formik-material-ui/commaSeparatedEmails';
import { FormikSwitch } from '../../../common/formik-material-ui/FormikSwitch';
import { trackSaveAddTeamSMSRule } from '../../../common/userTracking';
import HashTagSVG from '../../../images/hashtag.svg';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import {
  createHoursString,
  Scheduler,
} from '../../components/CadenceSettings/Scheduler';
import {
  getErrorTabIndex,
  isErrorInTab,
} from '../../components/modals/DevicesAndRoutingModal';
import { ModalWithStyles } from '../../components/modals/ModalWithStyles';
import { WizardModal } from '../../components/modals/WizardModal';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import { IBusinessSMS } from './BusinessSMSEditDialog';
import { BusinessSMSNumbers } from './BusinessSMSNumbers';
import { BusinessSMSTeams } from './BusinessSMSTeams';

// check if uri valid by reusing uri validator and useCallback to create uri closure only once
// TODO: move these utility to common folder
export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));
const useStyles = makeStyles((theme: Theme) => ({
  smsField: {
    minHeight: '3em',
  },
  spacing: {
    marginLeft: theme.spacing(1),
    marginTop: '13.5px',
  },
  timeframes: {
    marginTop: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    // width: '65px',
  },
  smallWidth: {
    maxWidth: '240px !important',
  },
  mediumWidth: {
    maxWidth: '400px !important',
  },
  actionTitle: {
    fontWeight: 300,
    margin: '6px 0px',
  },
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  bottomMargin: {
    marginBottom: '32px !important',
  },
  bottomMargin2: {
    marginBottom: '16px !important',
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  indent: {
    marginLeft: theme.spacing(1),
  },
  reset: {
    margin: 0,
    padding: 0,
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  tabs: {},
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  tabHeader: {
    width: '100%',
    '&.MuiTab-textColorInherit.Mui-disabled': {
      opacity: 1,
      '& .MuiTab-wrapper': {
        justifyContent: 'flex-start',
        margin: '10px 0',
      },
    },
  },
  tabError: {
    '& .MuiTabs-indicator': {
      backgroundColor: 'red',
    },
  },
  infoOutlinedIcon: {
    marginLeft: '4px',
    cursor: 'pointer',
    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#005C78',
  },
}));

const HashTagIcon = () => {
  return (
    <img
      alt="Numbers"
      style={{ height: 16, marginRight: '18.5px' }}
      src={HashTagSVG}
    />
  );
};

/**
 * Focus on invalid field using Formik
 * @see https://github.com/formium/formik/issues/146
 */
class ErrorFocus extends React.Component<any> {
  isObject(value: any) {
    return value && typeof value === 'object' && value.constructor === Object;
  }

  getKeysRecursively = (object: any): any => {
    if (!this.isObject(object)) {
      return '';
    }
    const currentKey = Object.keys(object)[0];
    if (!this.getKeysRecursively(object[currentKey])) {
      return currentKey;
    }
    return currentKey + '.' + this.getKeysRecursively(object[currentKey]);
  };

  componentDidUpdate(prevProps: any) {
    const { isSubmitting, isValidating, errors } = prevProps.formik;
    const keys = Object.keys(errors);
    if (keys.length > 0 && isSubmitting && !isValidating) {
      const selectorKey = this.getKeysRecursively(errors);
      const selector = `[id="${selectorKey}"], [name="${selectorKey}"] `;
      const errorElement: HTMLElement | null = document.querySelector(selector);
      errorElement?.focus();
    }
  }

  render() {
    return null;
  }
}

export const FormikErrorFocus = connect(ErrorFocus);

/**
 * EventCallback trigger on user action
 *
 * @callback eventCallback
 * @param {Object} event Synthetic react event
 */

interface IBusinessSMSRuleEditForm {
  id?: string;
  businessid?: string | number;
  show: boolean;
  businessSMSRule: IBusinessSMS;
  isLoading: boolean;
  onSubmit: any;
  onHide: () => void;
  formikRef?: MutableRefObject<FormikProps<any> | undefined>;
  formikValidator?: (values: FormikValues) => void | Record<string, unknown>;
  formikValidatorDebounceTimeout?: number;
  render?: React.FC<{ formik: FormikProps<any> }>;
  businessLoadable: any;
  businessCached: any;
}

/**
 * Business SMS rule form to edit settings/options
 *
 * @param {Object} props
 * @param {Object} props.id business S ID
 * @param {Object} props.businessSMSRule Object containing current business sms rule settings/options
 * @param {boolean} props.isLoading Set to true to show spinner
 * @param {eventCallback} props.onSubmit Callback to trigger on submit
 * @param {eventCallback} props.onHide Function to hide modal
 *
 * @see  https://stackoverflow.com/questions/48901016/how-to-set-change-field-value-from-external-user-action-react-final-form final-form mutators
 */
export function BusinessSMSEditForm({
  id,
  businessid,
  show,
  businessSMSRule,
  isLoading,
  onSubmit,
  onHide,
  render,
  formikRef,
  formikValidator,
  businessLoadable,
  businessCached,
  formikValidatorDebounceTimeout = 250,
}: IBusinessSMSRuleEditForm) {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(0);
  const handleChange = (formik: any) => async (event: any, newIndex: any) => {
    setTabIndex(+newIndex);
  };
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const { callback: debouncedCallback } = formikValidator
    ? useDebouncedCallback(formikValidator, formikValidatorDebounceTimeout)
    : { callback: undefined };

  const data = {
    call: methodNameEnum.GET_SMS_TEMPLATE,
  };
  const smsTemplates = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_SMS_TEMPLATE,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const smsTemplateOptions = useMemo(
    () =>
      smsTemplates.state === 'hasValue'
        ? [...(smsTemplates.contents?.response?.data?.result ?? [])]
            ?.filter((template) => template.hasCustomField === 0)
            .sort((a, b) =>
              a.smsTemplateName
                ?.toLocaleLowerCase()
                ?.localeCompare(b?.smsTemplateName?.toLocaleLowerCase())
            )
        : [],
    [smsTemplates]
  );

  const onSubmitHandler = (values: any) => {
    onSubmit({
      ...values,
      initialValues,
    });
  };

  const tabNames = [
    {
      title: 'Settings',
      icon: SettingsApplicationsIcon,
    },
    {
      title: 'Team SMS Rule Hours',
      icon: QueryBuilderIcon,
    },
    {
      title: 'Members',
      icon: GroupIcon,
    },
    {
      title: 'Numbers',
      icon: HashTagIcon,
    },
  ];
  // Validation schema (useMemo to prevent recreation)
  // Put here to be closer to initial values
  const BusinessSMSRuleEditSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required('Required'),
        days: Yup.number().required('Required'),
        minutes: Yup.number().required('Required'),
        hours: Yup.number().required('Required'),
        smsMessage: Yup.string().when('afterHoursActionType', {
          is: (afterHoursActionType) => afterHoursActionType === 'sendSMS',
          then: Yup.string().required('required'),
        }),
      }),
    []
  );

  const tabToFields = [
    ['name', 'hours', 'days', 'minutes'],
    [
      'useBusinessHoursForSMS',
      'smsHours',
      'smsMessage',
      'afterHoursActionType',
      'useCustomSMSMessage',
      'smsTemplateId',
      'smsMessage',
    ],
    ['teams'],
    ['numbers'],
  ];
  // state for business hours modal;
  const [showBusinessHours, setShowBusinessHours] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const handleShowScheduler = () => setShowScheduler(true);
  const onHideHours = () => {
    setShowScheduler(false);
  };

  const schedulableHoursCached = useRef();
  const handleSetSchedulableHours = (hours: any) => {
    schedulableHoursCached.current = hours;
  };
  // intialized values for Formik
  const initialValues = useMemo<any>(
    () => ({
      days: businessSMSRule?.days,
      minutes: businessSMSRule?.minutes,
      hours: businessSMSRule?.hours,
      businessId: businessSMSRule?.businessId,
      active: Number(businessSMSRule?.active) === 1,
      smsHours: businessSMSRule?.smsHours,
      useBusinessHoursForSMS:
        Number(businessSMSRule?.useBusinessHoursForSMS) === 1,
      afterHoursActionType: businessSMSRule?.afterHoursActionType,
      smsTemplateId: businessSMSRule?.smsTemplateId,
      smsMessage: businessSMSRule?.smsMessage,
      teams: businessSMSRule?.teams,
      numbers: businessSMSRule?.numbers,
      memberCount: businessSMSRule?.memberCount,
      useCustomSMSMessage: businessSMSRule?.useCustomSMSMessage === 1,
      name: businessSMSRule?.name,
      createdByUserId: businessSMSRule?.createdByUserId,
    }),
    [businessSMSRule]
  );

  return (
    <Formik
      innerRef={formikRef as MutableRefObject<FormikProps<any>>}
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={BusinessSMSRuleEditSchema}
      validateOnBlur={false} // validate onChange already on
      validate={debouncedCallback}
      onSubmit={onSubmitHandler}
    >
      {(formik) => {
        function handleSubmit() {
          if (!createHoursString(schedulableHoursCached.current)) {
            enqueueSnackbarWithStyles({
              message: 'Please select a time range',
              alertProps: {
                severity: 'warning',
              },
            });
          } else {
            formik.setFieldValue('smsHours', schedulableHoursCached.current);
            onHideHours();
          }
        }
        const smsHoursString = useMemo<string[]>(
          () =>
            createHoursString(
              formik.values.useBusinessHoursForSMS
                ? businessCached.current?.businessDetails?.businessHours
                : formik.values.smsHours
            ).split(','),
          [
            showBusinessHours,
            setShowBusinessHours,
            formik.values.useBusinessHoursForSMS,
            formik.values.smsHours,
            businessLoadable,
            businessCached,
          ]
        );
        const hideSMSTemplateId =
          formik.values.useCustomSMSMessage &&
          formik.values.smsTemplateId === initialValues.smsTemplateId &&
          initialValues.useCustomSMSMessage ===
            formik.values.useCustomSMSMessage;
        return (
          <form
            className="form form-label-right"
            onSubmit={formik.handleSubmit}
            noValidate
          >
            <TabContext value={String(tabIndex)}>
              <WizardModal
                title={businessSMSRule.name || 'Add New Team SMS Rule'}
                show={show}
                onHide={() => {
                  onHide();
                }}
                disableSubmit={!formik.dirty}
                isSubmitting={formik.isSubmitting}
                onSubmit={() => {
                  trackSaveAddTeamSMSRule();
                  formik.handleSubmit();
                }}
                isLoading={isLoading}
                navigation={
                  <>
                    <Tab
                      className={clsx(classes.tab, classes.tabHeader)}
                      label={businessSMSRule.name || 'Add New Team SMS Rule'}
                      disabled={true}
                    />
                    <TabList
                      onChange={handleChange(formik)}
                      aria-label="Team SMS Rule Form"
                      orientation="vertical"
                      variant="standard"
                      className={clsx(classes.tabs, {
                        [classes.tabError]: !formik.isValid,
                      })}
                    >
                      {tabNames.map((tabItem, index) => (
                        <Tab
                          {...{ name: tabItem.title }}
                          key={index}
                          className={classes.tab}
                          label={
                            <TabLabel
                              index={index}
                              formik={formik}
                              tabToFields={tabToFields}
                              tabItem={tabItem}
                            />
                          }
                          value={String(index)}
                        />
                      ))}
                    </TabList>
                  </>
                }
                dialogActions={
                  <>
                    <Button
                      className={classes.button}
                      disabled={formik.isSubmitting}
                      data-pup={'cancel-business-sms-rule-button'}
                      variant="contained"
                      disableElevation={true}
                      onClick={() => {
                        onHide();
                      }}
                      color="inherit"
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      className={clsx(classes.button)}
                      variant="contained"
                      data-pup={'save-business-sms-rule-button'}
                      disableElevation={true}
                      disabled={
                        formik.isSubmitting || !formik.dirty || !formik.isValid
                      }
                      onClick={(
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                      ) => {
                        // trigger show errors if errors exists
                        formik.handleSubmit(event as any);

                        // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                        if (
                          !formik.isValid && // if form invalid
                          !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                        ) {
                          const errorTabIndex = getErrorTabIndex(
                            formik.errors,
                            tabToFields
                          );
                          /**
                           * switch to tab containing error
                           *
                           * since form validation is in flight and makes page rendering updates
                           * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                           * putting state changes in a separate tick will prevent coliding page rendering updates
                           *
                           * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                           */
                          setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                        }
                      }}
                    >
                      Save
                    </Button>
                  </>
                }
              >
                <FormikErrorFocus />
                {/* General Settings */}
                <TabPanel value="0">
                  <article>
                    <FormikTextField
                      name="name"
                      dataPup="business-sms-rule-name"
                      className={clsx(
                        classes.field,
                        classes.bottomMargin,
                        classes.mediumWidth
                      )}
                      formik={formik}
                      inputProps={{ maxLength: 300 }}
                      fullWidth
                      label="Name"
                      size="small"
                      required
                      // validate={composeValidators(required())}
                      variant="outlined"
                    />
                    <div>
                      <span className={classes.actionTitle}>
                        Agent Assignment Time Limit
                      </span>
                      <Tooltip title="This controls the amount of time an agent has to reply to the team sms thread after inital reply. Upon expiration of the set time - the team sms thread will then be accessible by other agents assigned to the team sms rule.">
                        <InfoOutlinedIcon
                          className={classes.infoOutlinedIcon}
                        />
                      </Tooltip>
                    </div>
                    <span
                      style={{ marginBottom: '16px !important' }}
                      className={clsx(
                        classes.spacing,
                        'timeframe',
                        classes.bottomMargin2
                      )}
                    >
                      <LabelledOutline
                        className={classes.smallWidth}
                        label="After"
                        customPadding="5px 8px"
                      >
                        <FormikTextField
                          dataPup={'retryAfterDurationDays'}
                          formik={formik}
                          className={classes.timeframes}
                          name={`days`}
                          label="Days"
                          size="small"
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: 90,
                            },
                          }}
                          type="number"
                          variant="outlined"
                        />
                        <FormikTextField
                          dataPup={'retryAfterDurationHours'}
                          className={classes.timeframes}
                          formik={formik}
                          name={`hours`}
                          label="Hours"
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: 23,
                            },
                          }}
                          size="small"
                          type="number"
                          variant="outlined"
                        />
                        <FormikTextField
                          dataPup={'retryAfterDurationMinutes'}
                          className={classes.timeframes}
                          formik={formik}
                          name={`minutes`}
                          label="Minutes"
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: 59,
                            },
                          }}
                          size="small"
                          type="number"
                          variant="outlined"
                        />
                      </LabelledOutline>
                    </span>
                  </article>
                </TabPanel>
                {/* Messages */}
                <TabPanel value="1">
                  <div style={{ marginTop: '16px' }}>
                    <LabelledOutline label="Team SMS Rule Hours">
                      <div className="row">
                        <div className="col-lg-6">
                          Messages will be routed:
                          {smsHoursString.map((string, index) => (
                            <div key={index}>
                              {string} <br />
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            // paddingRight: '50px',
                            alignItems: 'center',
                          }}
                          className="col-lg-6"
                        >
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="useBusinessHoursForSMS"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                                dataPup={'useBusinessHoursSwitch'}
                              />
                            }
                            label="Use Business Hours"
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            className={classes.editButton}
                            onClick={handleShowScheduler}
                            disabled={formik.values.useBusinessHoursForSMS}
                          >
                            Edit Team SMS Rule Hours
                          </Button>
                        </div>
                      </div>
                    </LabelledOutline>
                    <div style={{ margin: '24px 0px 0px' }} />
                    <FormikSelect
                      name={`afterHoursActionType`}
                      className={classes.field}
                      formik={formik}
                      fullWidth
                      label={'Outside Team SMS Hours Action'}
                      size="small"
                      variant="outlined"
                      // defaultValue={defaultType}
                    >
                      <MenuItem value="sendSMS">Reply Back To SMS</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                    </FormikSelect>
                    <div style={{ margin: '16px 0px 0px' }} />
                    {formik.values.afterHoursActionType === 'sendSMS' && (
                      <div className="row">
                        <div className="col-sm-4">
                          <MaterialAutocomplete
                            type="text"
                            fullWidth
                            dataPup={`smsTemplateAutocomplete`}
                            label="SMS Template Name"
                            size="small"
                            variant="outlined"
                            multiple={false}
                            options={
                              smsTemplateOptions?.map((sms) =>
                                String(sms.smsTemplateid)
                              ) ?? []
                            }
                            value={
                              hideSMSTemplateId
                                ? ''
                                : String(formik.values.smsTemplateId)
                            }
                            onChange={(event, newValue, reason) => {
                              if (reason === 'clear') {
                                formik.setFieldValue(
                                  `smsTemplateId`,
                                  initialValues.smsTemplateId
                                );
                                formik.setFieldValue('smsMessage', '');
                                formik.setFieldValue(
                                  `useCustomSMSMessage`,
                                  businessSMSRule?.useCustomSMSMessage === 1
                                );
                              } else {
                                formik.setFieldValue(
                                  `smsTemplateId`,
                                  String(newValue)
                                );
                                formik.setFieldValue(
                                  `useCustomSMSMessage`,
                                  false
                                );
                                const selectedSMSTemplate = smsTemplateOptions?.find(
                                  (sms) =>
                                    String(sms.smsTemplateid) ===
                                    String(newValue)
                                );
                                formik.setFieldValue(
                                  'smsMessage',
                                  selectedSMSTemplate?.smsTemplateMessage
                                );
                              }
                            }}
                            getOptionLabel={(option) => {
                              return (
                                smsTemplateOptions?.find(
                                  (sms) =>
                                    String(sms.smsTemplateid) === String(option)
                                )?.smsTemplateName ?? ''
                              );
                            }} // collapse to empty string if no option found
                            isOptionEqualToValue={(option, value) => {
                              return option === value;
                            }}
                          />
                          {!hideSMSTemplateId && (
                            <FormControlLabel
                              style={{ marginTop: '5px' }}
                              control={
                                <FormikSwitch
                                  name={`useCustomSMSMessage`}
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                />
                              }
                              label="Customize Message"
                            />
                          )}
                        </div>
                        <div className="col-sm-8">
                          <FormikTextField
                            multiline={true}
                            formik={formik}
                            disabled={!formik.values?.useCustomSMSMessage}
                            name={`smsMessage`}
                            label="SMS Template Message"
                            size="small"
                            variant="outlined"
                            required
                            fullWidth
                            // className={classes.smsWidth}
                            InputProps={{
                              classes: {
                                input: classes.smsField,
                              },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <ModalWithStyles
                    onSubmit={handleSubmit}
                    title={'Business Hours'}
                    show={showScheduler}
                    onHide={onHideHours}
                    isSubmitting={formik.isSubmitting}
                    size="lg"
                  >
                    <Scheduler
                      schedulableHours={formik.values.smsHours}
                      setSchedulableHours={handleSetSchedulableHours}
                    />
                  </ModalWithStyles>
                </TabPanel>
                <TabPanel value="2">
                  <article>
                    <BusinessSMSTeams id={id} formik={formik} />
                  </article>
                </TabPanel>
                <TabPanel value="3">
                  <article>
                    <BusinessSMSNumbers
                      id={id}
                      formik={formik}
                      initialValues={initialValues}
                    />
                  </article>
                </TabPanel>

                {/* add hidden button to allow enter page */}
                <button hidden={true}>submit</button>
                {/* </ModalWithStyles> */}
              </WizardModal>
            </TabContext>
          </form>
        );
      }}
    </Formik>
  );
}
